import type { ReactElement } from "react";
import { Pencil, Power, Trash2 } from "lucide-react";
import { space } from "@tokens/index";
import { Badge, Text } from "@primitives/index";
import { DataRow, RowActions, type RowAction } from "../admin-ui";
import { taskTypeLabel } from "../workflows";
import type { RoutingRule } from "./types";

interface RuleRowProps {
    readonly rule: RoutingRule;
    readonly queueNamesById: ReadonlyMap<string, string>;
    readonly isToggling: boolean;
    readonly isDeleting: boolean;
    readonly onEdit: () => void;
    readonly onToggleEnabled: () => void;
    readonly onDelete: () => void;
    // When false, the row hides its write affordances (edit / toggle /
    // delete). The rule itself stays visible so non-admin queue members
    // can still see why work lands here. Defaults to true to keep the
    // master routing-rules page's behavior unchanged.
    readonly canEdit?: boolean;
    // When true, the row omits the "Target queue" sub-section. Used on
    // a queue's detail page, where the target is implicit and showing
    // it on every row is redundant noise.
    readonly hideTargetQueue?: boolean;
}

export function RuleRow({
    rule,
    queueNamesById,
    isToggling,
    isDeleting,
    onEdit,
    onToggleEnabled,
    onDelete,
    canEdit = true,
    hideTargetQueue = false,
}: RuleRowProps): ReactElement {
    const predicateChips = Object.entries(rule.tagPredicates);
    const hasEntityType =
        rule.entityType !== undefined && rule.entityType !== null && rule.entityType.length > 0;
    const hasTaskType =
        rule.taskType !== undefined && rule.taskType !== null && rule.taskType.length > 0;

    const actions: readonly RowAction[] = canEdit
        ? [
              { key: "edit", label: "Edit", icon: Pencil, onSelect: onEdit },
              {
                  key: "toggle",
                  label: rule.isEnabled ? "Disable" : "Enable",
                  icon: Power,
                  onSelect: onToggleEnabled,
                  disabled: isToggling,
              },
              {
                  key: "delete",
                  label: "Delete",
                  icon: Trash2,
                  onSelect: onDelete,
                  disabled: isDeleting,
                  tone: "danger",
              },
          ]
        : [];

    return (
        <DataRow
            title={rule.description}
            badges={
                <Badge variant={rule.isEnabled ? "success" : "neutral"} size="sm">
                    {rule.isEnabled ? "ENABLED" : "DISABLED"}
                </Badge>
            }
            meta={
                <div style={{ display: "flex", gap: space[2], flexWrap: "wrap", minWidth: 0 }}>
                    <Badge variant="accent" size="sm" caps={false} truncate>
                        {rule.workflowKind}
                    </Badge>
                    {hasEntityType && (
                        <Badge variant="neutral" size="sm" caps={false} truncate>
                            {rule.entityType}
                        </Badge>
                    )}
                    {hasTaskType && (
                        <Badge
                            variant="default"
                            size="sm"
                            caps={false}
                            truncate
                            title={`TaskType: ${rule.taskType ?? ""}`}
                        >
                            {taskTypeLabel(rule.taskType ?? "")}
                        </Badge>
                    )}
                </div>
            }
            trailing={
                actions.length > 0 ? (
                    <RowActions actions={actions} menuLabel={`Actions for ${rule.description}`} />
                ) : undefined
            }
        >
            <div style={{ display: "grid", gap: space[1] }}>
                <Text role="caption" tone="muted">
                    Predicates
                </Text>
                {predicateChips.length === 0 ? (
                    <Text role="caption" tone="muted">
                        (catch-all for this kind)
                    </Text>
                ) : (
                    <div style={{ display: "flex", gap: space[2], flexWrap: "wrap", minWidth: 0 }}>
                        {predicateChips.map(([k, v]) => (
                            <Badge key={k} variant="default" size="sm" caps={false} truncate>
                                {`${k}: ${v}`}
                            </Badge>
                        ))}
                    </div>
                )}
            </div>
            {!hideTargetQueue && (
                <div style={{ display: "grid", gap: space[1] }}>
                    <Text role="caption" tone="muted">
                        Target queue
                    </Text>
                    <div style={{ display: "flex", gap: space[2], flexWrap: "wrap", minWidth: 0 }}>
                        <Badge
                            variant="neutral"
                            size="sm"
                            caps={false}
                            truncate
                            title={queueNamesById.get(rule.targetQueueId) ?? rule.targetQueueId}
                        >
                            {queueNamesById.get(rule.targetQueueId) ?? rule.targetQueueId}
                        </Badge>
                    </div>
                </div>
            )}
        </DataRow>
    );
}
